import { FC, useEffect, useState } from 'react';
import {
  Mail,
  Server,
  Lock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  Power,
  RefreshCw,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui';
import api from '@/utils/api';
import { SendGridConfigForm } from './SendGridConfigForm';
import { SmtpConfigForm } from './SmtpConfigForm';
import { getErrorMessage } from '@/utils/error';

type EmailProvider = 'sendgrid' | 'smtp';

interface EmailConfig {
  id: string;
  providerType: EmailProvider;
  isActive: boolean;
  sendgridFromEmail?: string;
  sendgridSenderName?: string;
  sendgridApiKeyMasked?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsernameMasked?: string;
  smtpSecure?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const EmailConfigTab: FC = () => {
  const [configs, setConfigs] = useState<EmailConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal State
  const [showSendGridForm, setShowSendGridForm] = useState(false);
  const [showSmtpForm, setShowSmtpForm] = useState(false);

  // Action State
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchConfigs = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<EmailConfig[]>('/email-config');
      setConfigs(data);

      // Auto-cleanup: If multiple configs of same type exist, keep only the latest
      // This enforces the "1 SMTP + 1 SendGrid" rule if the backend doesn't
      const sendGrids = data.filter(c => c.providerType === 'sendgrid');
      const smtps = data.filter(c => c.providerType === 'smtp');

      if (sendGrids.length > 1) {
        // Keep first (latest due to backend sorting), delete rest
        for (let i = 1; i < sendGrids.length; i++) deleteConfig(sendGrids[i].id, true);
      }
      if (smtps.length > 1) {
        for (let i = 1; i < smtps.length; i++) deleteConfig(smtps[i].id, true);
      }

    } catch (err) {
      console.error('Failed to fetch configs:', err);
      setError('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const deleteConfig = async (id: string, silent = false) => {
    try {
      if (!silent) setProcessingId(id);
      await api.delete(`/email-config/${id}`);
      if (!silent) {
        setSuccess('Configuration removed');
        setTimeout(() => setSuccess(''), 3000);
        fetchConfigs();
      }
    } catch (err) {
      if (!silent) setError('Failed to remove configuration');
    } finally {
      if (!silent) setProcessingId(null);
    }
  };

  const activateConfig = async (id: string) => {
    try {
      setProcessingId(id);
      await api.post(`/email-config/${id}/activate`);
      setSuccess('Channel activated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchConfigs();
    } catch (err) {
      setError('Failed to activate channel');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessingId(null);
    }
  };

  const verifyConnection = async (id: string) => {
    try {
      setProcessingId(id);
      const res = await api.post(`/email-config/${id}/verify`);
      if (res.data.success) {
        setSuccess('Connection verified successfully');
      } else {
        setError(res.data.message || 'Verification failed');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessingId(null);
    }
  };

  // Derived State
  const sendGridConfig = configs.find(c => c.providerType === 'sendgrid');
  const smtpConfig = configs.find(c => c.providerType === 'smtp');
  // Render Helpers
  const renderProviderCard = (type: EmailProvider, config?: EmailConfig) => {
    const isSendGrid = type === 'sendgrid';
    const LabelIcon = isSendGrid ? Mail : Server;
    const title = isSendGrid ? 'SendGrid Integration' : 'SMTP Server';

    // Check if THIS specific config is the active one
    const isActive = config?.isActive;

    if (!config) {
      return (
        <div className="border border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-50/80 transition-colors h-full min-h-[280px]">
          <div className="bg-white p-4 rounded-full shadow-sm mb-4">
            <LabelIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-[200px]">
            {isSendGrid
              ? 'Connect your SendGrid account for reliable email delivery.'
              : 'Configure your own SMTP server for full control.'}
          </p>
          <Button
            variant="secondary"
            onClick={() => isSendGrid ? setShowSendGridForm(true) : setShowSmtpForm(true)}
            className="bg-white hover:bg-gray-100 border-gray-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Connect {isSendGrid ? 'SendGrid' : 'SMTP'}
          </Button>
        </div>
      );
    }

    return (
      <div className={`border rounded-xl p-6 h-full flex flex-col relative transition-all ${isActive
        ? 'border-blue-200 bg-blue-50/20 ring-1 ring-blue-100'
        : 'border-gray-200 bg-white hover:shadow-md'
        }`}>
        {isActive && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold border border-green-200">
            <div className="w-1.5 h-1.5 rounded-full bg-green-600" />
            Active
          </div>
        )}

        <div className="flex items-center gap-4 mb-6">
          <div className={`p-3 rounded-lg ${isSendGrid ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
            <LabelIcon className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                Connected
              </span>
              {!isActive && (
                <button
                  onClick={() => activateConfig(config.id)}
                  disabled={processingId === config.id}
                  className="text-xs font-medium text-blue-600 hover:underline hover:text-blue-700"
                >
                  Make Active
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="flex-1 space-y-4 mb-6">
          {isSendGrid ? (
            <>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase">From Email</span>
                <div className="text-sm font-medium text-gray-900 truncate">
                  {config.sendgridFromEmail || 'Not Set'}
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase">Sender Name</span>
                <div className="text-sm font-medium text-gray-900 truncate">
                  {config.sendgridSenderName || '-'}
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase">API Key</span>
                <div className="text-xs font-mono text-gray-600 bg-gray-50 p-1.5 rounded border border-gray-100 w-fit">
                  {config.sendgridApiKeyMasked}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-xs font-medium text-gray-500 uppercase">Host</span>
                  <div className="text-sm font-medium text-gray-900 truncate">{config.smtpHost}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium text-gray-500 uppercase">Port</span>
                  <div className="text-sm font-medium text-gray-900">{config.smtpPort}</div>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase">Username</span>
                <div className="text-sm font-medium text-gray-900 truncate">{config.smtpUsernameMasked}</div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase">Security</span>
                <div className="flex items-center gap-1.5 text-sm text-gray-700">
                  {config.smtpSecure ? <Lock className="w-3 h-3 text-green-500" /> : <AlertCircle className="w-3 h-3 text-orange-500" />}
                  {config.smtpSecure ? 'SSL/TLS' : 'None'}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-100 mt-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => verifyConnection(config.id)}
            disabled={processingId === config.id}
            className="flex-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50"
          >
            {processingId === config.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Verify
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => isSendGrid ? setShowSendGridForm(true) : setShowSmtpForm(true)}
            className="flex-1 text-gray-600 hover:bg-gray-100"
          >
            Edit
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => deleteConfig(config.id)}
            disabled={processingId === config.id}
            className="w-9 px-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
            title="Disconnect"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="space-y-10 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Email Integrations</h2>
        <p className="text-gray-500 mt-2">
          Manage your email delivery channels. You can configure both SendGrid and SMTP, but only one can be active at a time.
        </p>
      </div>

      {/* Notifications */}
      <div className="space-y-2">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            {success}
          </div>
        )}
      </div>

      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderProviderCard('sendgrid', sendGridConfig)}
        {renderProviderCard('smtp', smtpConfig)}
      </div>

      {/* Modals */}
      {(showSendGridForm || showSmtpForm) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h3 className="font-bold text-gray-900">
                Configure {showSendGridForm ? 'SendGrid' : 'SMTP Server'}
              </h3>
              <button
                onClick={() => { setShowSendGridForm(false); setShowSmtpForm(false); }}
                className="text-gray-400 hover:text-gray-600 bg-white p-1 rounded-full border border-gray-200 hover:border-gray-300 transition-all"
              >
                <Power className="w-4 h-4 rotate-45" />
              </button>
            </div>

            <div className="p-6">
              {showSendGridForm && (
                <>
                  <p className="text-sm text-gray-500 mb-6 bg-blue-50 p-3 rounded-lg border border-blue-100">
                    <span className="font-semibold text-blue-700">Note:</span> Saving a new configuration will replace any existing SendGrid settings.
                  </p>
                  <SendGridConfigForm
                    onSuccess={() => { setShowSendGridForm(false); fetchConfigs(); setSuccess('SendGrid configured successfully'); }}
                    onCancel={() => setShowSendGridForm(false)}
                  />
                </>
              )}

              {showSmtpForm && (
                <>
                  <p className="text-sm text-gray-500 mb-6 bg-purple-50 p-3 rounded-lg border border-purple-100">
                    <span className="font-semibold text-purple-700">Note:</span> Saving a new configuration will replace any existing SMTP settings.
                  </p>
                  <SmtpConfigForm
                    onSuccess={() => { setShowSmtpForm(false); fetchConfigs(); setSuccess('SMTP configured successfully'); }}
                    onCancel={() => setShowSmtpForm(false)}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

